/* eslint-disable import/max-dependencies */
/* eslint-disable max-lines */
import { useTranslate } from '@im/react-localization';
import type { EndUserSearchResponse } from '../../hooks/exceptionDeal';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadIcon from '@mui/icons-material/FileUploadOutlined';
import React, { useState } from 'react';
import {
  DeatilsResponse,
  LineDetails,
  useDealDetailsDownloadFile,
  useDealsDeactivate,
} from '../../hooks/deal-details';
import { useNavigate } from 'react-router';
import DeactivateWarningDialog from '../../SimpleSearch/components/Dialog/DeactivateWarningDialog';
import DialogComponent from './DialogComponent';
import UploadDiscountDialog from '../UploadDiscountDialog';
import { useAccount } from '@azure/msal-react';
import {
  useEscalateED,
  useGetEDApproveReject,
} from '../../hooks/exceptionDeal';
import {
  mapSubmitForReviewRequest,
  SubmitForReviewRequest,
  useSubmitForReview,
} from '../../hooks/submit-for-review';
import { useRoles } from 'src/provider/UserRoleProvider';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { CenterLoader } from '@im/ui-lib/CenterLoader';
import { downloadFile } from 'src/util/downloadFileUtil';
import useSnackBar from 'src/hooks/useSnackBar';
import { DownloadMultipleFiles } from 'src/components/Download/DownloadMultipleFiles';

export default function DealDetailsPageHeaderButtons(
  params: Readonly<{
    countryCode: string;
    dealStatus?: string;
    details: DeatilsResponse | undefined;
    edAcopEndUserId?: string;
    edResellerId?: string;
    endUserOptionSelected: EndUserSearchResponse | null;
    exception: boolean;
    isMiamiChecked: boolean;
    role?: boolean;
    setIsMiamiChecked: React.Dispatch<React.SetStateAction<false | true>>;
  }>
) {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const { details, exception, role } = params;
  const [searchParams] = useSearchParams();
  const paramsReseller = searchParams.get('edResellerId');
  const paramsAcopId = searchParams.get('edAcopEndUserId');
  const customerset_id = searchParams.get('customerset_id');
  const businessType = searchParams.get('businessType');
  const [openEscalate, setOpenEscalate] = useState<boolean>(false);
  const [openSubmitForReview, setOpenSubmitForReview] =
    useState<boolean>(false);
  const [openAcceptRejectEdits, setOpenAcceptRejectEdits] =
    useState<boolean>(false);
  const [uploadDialog, setUploadDialog] = useState<boolean>(false);
  const [warnDeactivate, setWarnDeactivate] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  const [acceptReject, setAcceptReject] = useState<boolean>(false);
  const [checked, setChecked] = useState<boolean>(false);
  const [triggerAutoQuote, setTriggerAutoQuote] = useState<boolean>(
    details?.autoquotegenerated ?? false
  );
  const { isVBMUser } = useRoles();
  const downloadUrl = useDealDetailsDownloadFile({
    countryCode: params.countryCode,
    importreferenceid: details?.importreferenceid ?? 0,
  });
  const user: string = useAccount()?.username ?? '';
  const escalateED = useEscalateED({
    dealid: String(details?.deal_id),
    importreferenceid: String(details?.importreferenceid),
    notes: notes,
    userid: user,
  });
  const approveReject = useGetEDApproveReject(
    String(details?.importreferenceid)
  );
  const deactivateDeals = useDealsDeactivate(details?.importreferenceid ?? 0, {
    dealnumber: details?.deal_number ?? '',
    dealversion: details?.version ?? 0,
    editor: user,
  });
  const submitForReview = useSubmitForReview();
  const handleDeactivate = (reason?: string) => {
    deactivateDeals.mutateAsync(reason ?? '').finally(() => {
      navigate(-1);
      setWarnDeactivate(false);
    });
  };
  const handleUploadCloseDialog = () => {
    setUploadDialog(false);
  };
  const onEscalateSubmit = () => {
    escalateED
      .mutateAsync()
      .then((response) => undefined)
      .catch((error) =>
        showSnackBar({
          autoHide: 2000,
          message: error.response?.message ?? translate('Common.ActionError'),
          severity: 'error',
        })
      );
  };

  const onSubmitForReview = (
    userAction: SubmitForReviewRequest['userAction'] = 'S'
  ) => {
    const record = params.endUserOptionSelected ?? undefined;

    return submitForReview
      .mutateAsync(
        mapSubmitForReviewRequest({
          autoQuote: details?.autoQuote ?? '',
          businessType: businessType ?? '',
          checked,
          coG_Approval: isVBMUser ? 'N' : details?.coG_Approval ?? '',
          countryCode: record?.address[0]?.countryCode ?? params.countryCode,
          customersetid: String(customerset_id),
          deal_id: details?.deal_id ?? 0,
          dealnumber: details?.deal_number ?? '',
          dealversion: details?.version ?? 0,
          discounttype: details?.discounttype ?? '',
          edResellerId: String(params.edResellerId),
          importreferenceid: details?.importreferenceid ?? 0,
          isautoquoterequired: triggerAutoQuote,
          notes,
          quoteNumber: details?.quoteNumber ?? '',
          record,
          role,
          user,
          userAction,
        })
      )
      .then(() => true)
      .catch((error) =>
        showSnackBar({
          autoHide: 2000,
          message: error.response?.message ?? translate('Common.ActionError'),
          severity: 'error',
        })
      );
  };

  const hasSubscriptionWithSku = (data: LineDetails[] | undefined): boolean => {
    if (!Array.isArray(data)) return false;

    return data.every((item) => {
      if (item.product_business_type === 'Subscription') {
        return item.im_sku && item.im_sku.trim() !== '';
      }
      return true;
    });
  };

  const onAcceptReject = () => {
    const appRej = approveReject
      .mutateAsync({
        businessType: businessType ?? '',
        customersetids: String(customerset_id),
        dealids: String(details?.deal_id),
        dealnumbers: String(details?.deal_number),
        dealversions: String(details?.version),
        importreferenceids: String(details?.importreferenceid),
        isapproved: acceptReject,
        notes: notes,
        servicePlanResolved:
          businessType === 'Hybrid'
            ? hasSubscriptionWithSku(details?.lines)
            : false,
        userid: user,
        vendornames: String(details?.vendor_name),
      })
      .then((res) => {
        return true;
      })
      .catch((error) =>
        showSnackBar({
          autoHide: 2000,
          message: error.response?.message ?? translate('Common.ActionError'),
          severity: 'error',
        })
      );
    return appRej;
  };
  const onUser = () => {
    if (paramsAcopId || paramsReseller) {
      onSubmitForReview(acceptReject ? 'A' : 'R').then((value) => {
        if (value) onAcceptReject();
      });
    } else {
      onAcceptReject();
    }
  };
  const disableSubmitAcceptRejectButton =
    !params.edResellerId ||
    params.dealStatus === 'Deactivated' ||
    details?.statusid === 17;
  const showDownloadingInfo = () => {
    showSnackBar({
      autoHide: 'stay',
      message: translate('Common.Downloading'),
      severity: 'info',
    });
  };
  const canAcceptRejectEdits: boolean =
    Boolean(role) || Boolean(isVBMUser) || details?.coG_Approval === 'N';

  return (
    <Box p={1}>
      {(escalateED.isLoading ||
        approveReject.isLoading ||
        submitForReview.isLoading ||
        deactivateDeals.isLoading) && <CenterLoader overlay={true} />}
      <Stack direction="row" spacing={1}>
        {!exception && businessType !== 'Subscription' && (
          <DownloadMultipleFiles
            headerId={Number(
              details?.manaualHeaderId === 0 ||
                details?.manaualHeaderId === null
                ? details?.importreferenceid
                : details?.manaualHeaderId
            )}
            label={translate('BidLifeCycle.DownloadPaperWork')}
          />
        )}
        {role &&
          !exception &&
          businessType !== 'Subscription' &&
          details?.bidstatus === 'A' && (
            <Button
              data-testid="gds-vp-ss-header-deactivate"
              disabled={params.dealStatus === 'Deactivated'}
              onClick={() => setWarnDeactivate(true)}
              sx={(theme) => ({ color: theme.palette.error[700] })}
              variant="newOutlined"
            >
              {translate('VendorDeals.ActionDeactivate')}
            </Button>
          )}
        {businessType !== 'Subscription' && (
          <Button
            data-testid="gds-vp-ss-header-download"
            onClick={() => {
              showDownloadingInfo();
              downloadUrl
                .mutateAsync()
                .then((res) => {
                  downloadFile(
                    res,
                    `DealDetails-${details?.importreferenceid}.xlsx`
                  );
                  showSnackBar({
                    autoHide: 2000,
                    message: translate('Common.DownloadSuccess'),
                    severity: 'success',
                  });
                })
                .catch(() => {
                  showSnackBar({
                    autoHide: 2000,
                    message: translate('Common.FileNotFound'),
                    severity: 'error',
                  });
                });
            }}
            startIcon={<FileDownloadIcon />}
            variant={exception ? 'newText' : 'newOutlined'}
          >
            {translate('Common.Download')}
          </Button>
        )}
        {!exception && role && businessType !== 'Subscription' && (
          <Button
            data-testid="gds-vp-ss-header-upload"
            disabled={
              !details?.vendor_program_id || params.dealStatus === 'Deactivated'
            }
            onClick={() => setUploadDialog(true)}
            startIcon={<FileUploadIcon />}
            variant="newOutlined"
          >
            {translate('Common.Upload')}
          </Button>
        )}
        {exception && params.dealStatus === 'Pending review' && !role && (
          <Button
            data-testid="gds-vp-ss-header-escalate"
            onClick={() => setOpenEscalate(true)}
            sx={(theme) => ({ color: theme.palette.error[700] })}
            variant="newOutlined"
          >
            {translate('VendorDeals.ActionEscalate')}
          </Button>
        )}
        {exception &&
          !canAcceptRejectEdits &&
          params.dealStatus !== 'Escalated - high priority' &&
          params.dealStatus !== 'Pending review' &&
          params.dealStatus !== 'Deactivated' && (
            <Button
              data-testid="gds-vp-ss-header-subitforreview"
              disabled={disableSubmitAcceptRejectButton}
              onClick={() => setOpenSubmitForReview(true)}
              variant="newContained"
            >
              {translate('VendorDeals.ActionSubmitForReview')}
            </Button>
          )}

        <>
          {exception && canAcceptRejectEdits && (
            <Button
              data-testid="gds-vp-ss-header-rejectedit"
              disabled={disableSubmitAcceptRejectButton}
              onClick={() => {
                setAcceptReject(false);
                setOpenAcceptRejectEdits(true);
              }}
              variant="newOutlined"
            >
              {translate('VendorDeals.ActionRejectEdits')}
            </Button>
          )}
          {exception && canAcceptRejectEdits && (
            <Button
              data-testid="gds-vp-ss-header-acceptedit"
              disabled={disableSubmitAcceptRejectButton}
              onClick={() => {
                setAcceptReject(true);
                setOpenAcceptRejectEdits(true);
              }}
              variant="newContained"
            >
              {translate('VendorDeals.ActionAcceptEdits')}
            </Button>
          )}
        </>

        {openEscalate && (
          <DialogComponent
            handleClose={() => setOpenEscalate(false)}
            message={translate('VendorDeals.ActionEscalateMessage')}
            notes={notes}
            onSubmit={onEscalateSubmit}
            requiredNotes={true}
            setNotes={setNotes}
            title={translate('VendorDeals.ActionEscalateTitle')}
          />
        )}
        {openSubmitForReview && (
          <DialogComponent
            checkBoxMessage={translate('VendorDeals.MarkHighPriority')}
            checked={checked}
            handleClose={() => setOpenSubmitForReview(false)}
            message={translate('VendorDeals.ActionSubmitForReviewMessage')}
            notes={notes}
            onSubmit={onSubmitForReview}
            requiredNotes={false}
            setChecked={setChecked}
            setNotes={setNotes}
            title={translate('VendorDeals.ActionSubmitForReviewTitle')}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={!triggerAutoQuote}
                  onChange={({ target: { checked } }) => {
                    setTriggerAutoQuote(!checked);
                  }}
                  sx={{ background: 'none !important' }}
                />
              }
              label={translate('ExceptionDeals.SkipAutoQuote')}
              sx={{ display: 'block' }}
            />
          </DialogComponent>
        )}
        {openAcceptRejectEdits && (
          <DialogComponent
            handleClose={() => setOpenAcceptRejectEdits(false)}
            message={translate('ExceptionDeals.AddReviewNotes')}
            notes={notes}
            onSubmit={onUser}
            requiredNotes={false}
            setNotes={setNotes}
            title={translate(
              `VendorDeals.Action${
                acceptReject ? 'AcceptEdits' : 'RejectEdits'
              }`
            )}
          />
        )}
        <DeactivateWarningDialog
          content={translate('VendorDeals.ActionDeactivateMessage')}
          handleClose={() => setWarnDeactivate(false)}
          handleDeactivate={handleDeactivate}
          isLoading={deactivateDeals.isLoading}
          isReasonRequired={true}
          open={warnDeactivate}
          title={translate('VendorDeals.ActionDeactivateTitle')}
        />
        {uploadDialog && (
          <UploadDiscountDialog
            handleUploadCloseDialog={handleUploadCloseDialog}
            rowData={{
              programId: details?.vendor_program_id,
              programName: details?.vendor_program_name,
            }}
            vendorId={details?.vendor_id}
            vendorName={details?.vendor_name}
          />
        )}
      </Stack>
    </Box>
  );
}
